import { useRef, useState, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  Briefcase,
  Calendar,
  ChartColumn,
  Code,
  FlaskConical,
  FolderClosed,
  Globe,
  Heart,
  ImagePlus,
  Lightbulb,
  Megaphone,
  Palette,
  Rocket,
  Server,
  Shield,
  Target,
  Users,
  Wrench,
  X,
  type LucideProps,
} from 'lucide-react';
import { Spinner, Tip, toast } from '@vatman/ui';
import { ApiError, api } from '../../lib/api';
import { errorText } from '../../i18n';

/**
 * A space's icon (crates/server/src/api/spaces.rs, clean_icon): one of the presets below
 * (`icon:rocket`), a picture someone uploaded (`/files/i/…`), or an emoji typed before presets.
 */
export const SPACE_ICONS: Record<string, ComponentType<LucideProps>> = {
  folder: FolderClosed,
  rocket: Rocket,
  code: Code,
  server: Server,
  palette: Palette,
  megaphone: Megaphone,
  chart: ChartColumn,
  users: Users,
  briefcase: Briefcase,
  lightbulb: Lightbulb,
  target: Target,
  shield: Shield,
  wrench: Wrench,
  book: BookOpen,
  flask: FlaskConical,
  calendar: Calendar,
  globe: Globe,
  heart: Heart,
};

/** The icon as it is drawn; `fallback` when the space has none. */
export function SpaceIcon({ icon, size = 16, fallback = null }: { icon: string | null | undefined; size?: number; fallback?: React.ReactNode }) {
  if (!icon) return <>{fallback}</>;
  const Preset = icon.startsWith('icon:') ? SPACE_ICONS[icon.slice(5)] : undefined;
  if (Preset) return <Preset size={size} />;
  if (icon.startsWith('/files/i/')) return <img className="space-icon-img" src={icon} alt="" width={size} height={size} draggable={false} />;
  if (icon.startsWith('icon:')) return <>{fallback}</>;
  return <span className="space-icon-emoji">{icon}</span>;
}

/** An emoji icon can go into plain text (a select option); presets and pictures cannot. */
export function iconText(icon: string | null | undefined): string {
  return icon && !icon.startsWith('icon:') && !icon.startsWith('/files/') ? `${icon} ` : '';
}

/** Shrinks a picture to a square of `px` (cropped to the middle) and encodes it as PNG. */
async function squarePicture(file: File, px: number): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const side = Math.min(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = px;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side, 0, 0, px, px);
  bitmap.close();
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode'))), 'image/png'));
}

/** Pick a preset, upload a picture of one's own, or go without an icon. */
export function SpaceIconPicker({ value, onChange }: { value: string; onChange: (icon: string) => void }) {
  const { t } = useTranslation();
  const file = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const upload = async (f: File) => {
    setBusy(true);
    try {
      const body = await squarePicture(f, 96);
      const res = await api.upload<{ icon: string }>('/spaces/icons', new File([body], 'icon.png', { type: 'image/png' }));
      onChange(res.icon);
    } catch (e) {
      toast(e instanceof ApiError ? errorText(e.code) : t('spaces.iconUnreadable'));
    } finally {
      setBusy(false);
    }
  };
  const custom = value && !value.startsWith('icon:') ? value : null;
  return (
    <div className="space-icon-picker" role="radiogroup" aria-label={t('spaces.icon')}>
      {Object.entries(SPACE_ICONS).map(([name, Icon]) => (
        <button key={name} type="button" role="radio" aria-checked={value === `icon:${name}`} aria-label={name} className="space-icon-option" data-active={value === `icon:${name}`} onClick={() => onChange(`icon:${name}`)}>
          <Icon size={18} />
        </button>
      ))}
      {custom && (
        <button type="button" role="radio" aria-checked className="space-icon-option" data-active>
          <SpaceIcon icon={custom} size={20} />
        </button>
      )}
      <Tip label={t('spaces.iconUpload')}>
        <button type="button" className="space-icon-option" aria-label={t('spaces.iconUpload')} disabled={busy} onClick={() => file.current?.click()}>
          {busy ? <Spinner /> : <ImagePlus size={18} />}
        </button>
      </Tip>
      {value && (
        <Tip label={t('spaces.iconNone')}>
          <button type="button" className="space-icon-option" aria-label={t('spaces.iconNone')} onClick={() => onChange('')}>
            <X size={18} />
          </button>
        </Tip>
      )}
      <input
        ref={file}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) void upload(f);
        }}
      />
    </div>
  );
}
